import {
  COLLECTION_ALL_STORAGE_KEY,
  FILTER_OPTION_SELECTOR,
  FILTER_TAG_KEY,
} from '../constants/filter-tree';
import { getTemplate } from '../template';
import {
  debounce,
  debug,
  getLimitOptionValues,
  getLocalStorage,
  simpleMd5,
  stripHtml,
  xssFilterOption,
} from '../utils';
import { getFilterSettings, getSelectedKeysFilter, keepStateMultipleLevelOptionItems } from '.';

/**
 * @type {import('./index.d').renderOptionMultiLevelCollection}
 */
export async function renderOptionMultiLevelCollection(context, dom, option, limit) {
  if (!dom) return;
  const selectedKeys = getSelectedKeysFilter(context);
  if (context.app.generalSettings.collection_id) {
    selectedKeys[`${option.filterOptionId}-${context.app.generalSettings.collection_id}`] = true;
  }

  if (context.app.generalSettings?.collection_tags?.length > 0) {
    selectedKeys[`${FILTER_TAG_KEY}-${context.app.generalSettings?.collection_tags[0]}`] = true;
  }

  const classNameUnselected = `boost-sd__filter-option-item-button boost-sd__filter-option-item-button--as-link`;
  const classNameSelected = classNameUnselected + ' boost-sd__filter-option-item-button--selected';

  let html = '';
  const size = limit && option.values.length > limit ? limit : option.values.length;
  const isXssDisabled = context.app?.xss?.filter?.disabled;
  const values = isXssDisabled ? getLimitOptionValues(option, size) : xssFilterOption(option, size);
  const showDocCount = getFilterSettings(context)?.showFilterOptionCount;

  if (option.filterType === 'collection') {
    // if enable settings show All in collection option
    if (option.activeCollectionAll) {
      const getIdCollectionAll = getLocalStorage(COLLECTION_ALL_STORAGE_KEY) || 0;
      const valueCollectionAll = window.boostSdCustomCollectionALl || {
        key: getIdCollectionAll,
        label: 'All',
        displayName: 'All',
        handle: 'all',
      };

      values?.unshift(valueCollectionAll);
    }
  }

  for (const value of values) {
    const actionId = simpleMd5(`${option.filterOptionId}-${value.key}`);
    const isDisabled = option.keepValuesStatic && value.doc_count === 0;
    let className = classNameUnselected;
    value.isDisabled = isDisabled;

    if (selectedKeys[`${option.filterOptionId}-${value.key}`]) {
      className = classNameSelected;
    }

    const metaData = {
      action: {
        optionCollection: {
          key: option.filterOptionId,
          value: value.key,
          label: option.label,
          filterType: option.filterType,
          displayName: value.displayName || value.label,
          handle: value.handle,
          actionId,
        },
      },
    };

    if (value.tags?.length > 0) {
      value.existedTags = true;
      const actionIdArrow = simpleMd5(`${option.filterOptionId}-arrow-${value.key}`);
      const targetIdList = simpleMd5(`${option.filterOptionId}-target-${value.key}`);
      const targetIdArrow = simpleMd5(`${option.filterOptionId}-target-arrow-${value.key}`);

      value.actionIdArrow = actionIdArrow;
      value.targetIdList = targetIdList;
      value.targetIdArrow = targetIdArrow;

      value.metaDataArrow = {
        action: {
          expandCollapseMultiLevel: {
            actionId: actionIdArrow,
            targets: [
              {
                targetId: targetIdList,
                classToggle: 'boost-sd__g-hide',
                shouldAddClass: true,
              },
              {
                targetId: targetIdArrow,
                classToggle: 'boost-sd__arrow-icon--down',
                shouldAddClass: false,
              },
            ],
          },
        },
      };

      value.tags.forEach((_tag) => {
        // assign actionId and metadata level2
        _tag.actionId = simpleMd5(`${FILTER_TAG_KEY}-${_tag.tag}`);
        _tag.targetId = simpleMd5(`${FILTER_TAG_KEY}-target-${_tag.tag}`);

        _tag.metaData = {
          action: {
            optionCollection: {
              key: option.filterOptionId,
              value: value.key,
              displayName: value.displayName,
              collectionTag: stripHtml(_tag.tag),
              label: option.label,
              handle: value.handle,
              tagDisplayName: _tag.displayName,
              filterType: option.filterType,
              valueDisplay: _tag.label || '',
              actionId: _tag.actionId,
            },
          },
        };

        _tag.className = classNameUnselected;
        if (selectedKeys[`${FILTER_TAG_KEY}-${_tag?.tag?.toLowerCase()}`]) {
          _tag.className = classNameSelected;
        }

        if (_tag.subTags?.length > 0) {
          _tag.existedSubTags = true;
          const actionIdArrowLevel2 = simpleMd5(
            `${option.filterOptionId}-arrow-level2-${_tag.tag}`
          );
          const targetIdListLevel2 = simpleMd5(
            `${option.filterOptionId}-target-level2-${_tag.tag}`
          );
          const targetIdArrowLevel2 = simpleMd5(
            `${option.filterOptionId}-target-arrow-level2-${_tag.tag}`
          );

          _tag.actionIdArrow = actionIdArrowLevel2;
          _tag.targetIdList = targetIdListLevel2;
          _tag.targetIdArrow = targetIdArrowLevel2;

          _tag.metaDataArrow = {
            action: {
              expandCollapseMultiLevel: {
                actionId: actionIdArrowLevel2,
                targets: [
                  {
                    targetId: targetIdListLevel2,
                    classToggle: 'boost-sd__g-hide',
                    shouldAddClass: true,
                  },
                  {
                    targetId: targetIdArrowLevel2,
                    classToggle: 'boost-sd__arrow-icon--down',
                    shouldAddClass: false,
                  },
                ],
              },
            },
          };

          _tag.subTags.forEach((_subTag) => {
            // assign actionId and metadata level2
            _subTag.actionId = simpleMd5(`${FILTER_TAG_KEY}-${_subTag.tag}`);
            _subTag.targetId = simpleMd5(`${FILTER_TAG_KEY}-target-${_subTag.tag}`);
            _subTag.metaData = {
              action: {
                optionCollection: {
                  key: option.filterOptionId,
                  value: value.key,
                  displayName: value.displayName,
                  collectionTag: stripHtml(_subTag.tag),
                  label: option.label,
                  handle: value.handle,
                  tagDisplayName: _subTag.displayName,
                  filterType: option.filterType,
                  valueDisplay: _subTag.label || '',
                  actionId: _subTag.actionId,
                },
              },
            };

            _subTag.className = classNameUnselected;
            if (selectedKeys[`${FILTER_TAG_KEY}-${_subTag?.tag?.toLowerCase()}`]) {
              _subTag.className = classNameSelected;
            }
          });
        }
      });
    }

    const locale = (window?.Shopify?.routes?.root || '/')?.replace(/\//g, '');

    html += await context.templateRender(getTemplate(context).filterOptionMultiLevelCollection, {
      option,
      actionId,
      metaData,
      value,
      className,
      locale,
      showDocCount: value.handle === 'all' ? false : showDocCount,
    });
  }

  dom.innerHTML = html;

  keepStateMultipleLevelOptionItems(context);

  // create custom events - after render
  const renderFiltered = new CustomEvent('afterRenderHtmlForFilter');
  window.dispatchEvent(renderFiltered);
}

/**
 * @type {import('./index.d').lazyLoadOptionMultiLevelCollection}
 */
export function lazyLoadOptionMultiLevelCollection(context, dom, id, option) {
  const domMouseMove = dom?.closest(FILTER_OPTION_SELECTOR);

  const mousemoveRender = debounce(() => {
    debug(context);

    renderFullOptionMultiLevelCollection(context, dom, id, option);

    domMouseMove.removeEventListener('mousemove', mousemoveRender);
    domMouseMove.removeEventListener('touchstart', mousemoveRender);
  }, 200);

  domMouseMove && domMouseMove.addEventListener('mousemove', mousemoveRender, { passive: true });
  domMouseMove && domMouseMove.addEventListener('touchstart', mousemoveRender, { passive: true });
}

/**
 * Render full option Multi Level Collection
 * @type {import('./index.d').renderFullOptionMultiLevelCollection}
 */
export function renderFullOptionMultiLevelCollection(context, dom, id, option) {
  if (
    context.filterTreeViewPort &&
    context.filterTreeViewPort[id] &&
    context.filterTreeViewPort[id].loaded < context.filterTreeViewPort[id].total &&
    context.filterTreeViewPort[id].displayType === 'list'
  ) {
    renderOptionMultiLevelCollection(context, dom, option, context.filterTreeViewPort[id].total);
    context.filterTreeViewPort[id] = {
      loaded: context.filterTreeViewPort[id].total,
      total: option.values.length,
      displayType: 'list',
    };
  }
}
